<template>
  <div class="container" :style="{ display: visible ? 'block' : 'none' }">
    <div class="header">
      <RotateLeftOutlined class="icon" @click.stop="handleRotate('left')" />
      <RotateRightOutlined class="icon" @click.stop="handleRotate('right')" />
      <ZoomOutOutlined class="icon" @click.stop="handleSmall" />
      <ZoomInOutlined class="icon" @click.stop="handleLarge" />
      <CloseOutlined class="icon" @click.stop="handleClose" />
    </div>
    <div class="content">
      <div
        :class="['arrow', 'leftArrow', currentIndex === 0 ? 'mouseBan' : 'mouseAllow']"
        @click.stop="handlePrevious"
      >
        <auth-img :src="currentIndex === 0 ? leftArrowDark : leftArrow" class="icon" />
      </div>
      <div
        class="image"
        :style="{
          transform: `translate3d(${position.x}px, ${position.y}px, 0)`
        }"
      >
        <auth-img
          ref="imgRef"
          :src="imageList[currentIndex]?.url"
          :style="{
            transform: `rotate(${currentRotate}deg) scale(${currentScale})`
          }"
          @mousedown="handleMouseDown"
          :draggable="false"
        />
      </div>

      <div
        :class="[
          'arrow',
          'rightArrow',
          currentIndex === imageList?.length - 1 ? 'mouseBan' : 'mouseAllow'
        ]"
        @click.stop="handleNext"
      >
        <auth-img
          :src="currentIndex === imageList?.length - 1 ? rightArrowDark : rightArrow"
          class="icon"
        />
      </div>
    </div>
  </div>
</template>
<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  watch,
  onMounted,
  onUnmounted,
  defineExpose,
  reactive
} from 'vue'
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
  RotateRightOutlined,
  RotateLeftOutlined
} from '@ant-design/icons-vue'
import leftArrow from '@/assets/img/chat/left-arrow.png'
import leftArrowDark from '@/assets/img/chat/left-arrow-dark.png'
import rightArrow from '@/assets/img/chat/right-arrow.png'
import rightArrowDark from '@/assets/img/chat/right-arrow-dark.png'
import getFixScaleEleTransPosition from './utils.js'
import { onBeforeUnmount } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  imageList: {
    type: Array,
    default: () => []
  },
  currentId: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['setVisible'])
const currentIndex = ref(0)
const currentScale = ref(1)
const currentRotate = ref(0)
const currentMsgId = ref('')

const isDragging = ref(false)
const imgRef = ref(null)
const position = reactive({
  x: 0,
  y: 0
})
const originPositionRef = reactive({
  originX: 0,
  originY: 0,
  deltaX: 0,
  deltaY: 0
})

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

watch(
  () => props.imageList,
  val => {
    if (currentMsgId.value) {
      currentIndex.value = val.findIndex(item => item.msgId === currentMsgId.value)
    }
  },
  { deep: true }
)

watch(
  () => props.currentId,
  val => {
    if (val) {
      currentMsgId.value = props.currentId
      currentIndex.value = props.imageList.findIndex(item => item.msgId === val)
      initState()
    }
  }
)

const initState = () => {
  currentScale.value = 1
  currentRotate.value = 0

  position.x = 0
  position.y = 0
}

// 键盘左右箭头控制切图
const handleKeydown = event => {
  if (event.key === 'ArrowLeft' || event.keyCode === 37) handlePrevious()
  else if (event.key === 'ArrowRight' || event.keyCode === 39) handleNext()
}

// 上一张
const handlePrevious = () => {
  if (currentIndex.value === 0) return
  currentMsgId.value = props.imageList[currentIndex.value - 1].msgId
  currentIndex.value -= 1
  initState()
}

// 下一张
const handleNext = () => {
  if (currentIndex.value === props.imageList.length - 1) return
  currentMsgId.value = props.imageList[currentIndex.value + 1].msgId
  currentIndex.value += 1
  initState()
}

// 缩小
const handleSmall = () => {
  if (currentScale.value <= 0.3) return
  currentScale.value -= 0.3
}

// 放大
const handleLarge = () => {
  currentScale.value += 0.3
}

// 旋转
const handleRotate = direction => {
  currentRotate.value = direction === 'right' ? currentRotate.value + 90 : currentRotate.value - 90
}

// 关闭预览
const handleClose = () => {
  initState()
  emit('setVisible', false)
}

// 拖拽开始
const handleMouseDown = event => {
  // Only allow main button
  if (event.button !== 0) return
  event.preventDefault()
  // Without this mask close will abnormal
  event.stopPropagation()
  originPositionRef.deltaX = event.pageX - position.x
  originPositionRef.deltaY = event.pageY - position.y
  originPositionRef.originX = position.x
  originPositionRef.originY = position.y
  isDragging.value = true
}

// 拖拽移动
const handleMouseMove = event => {
  if (props.visible && isDragging.value) {
    position.x = event.pageX - originPositionRef.deltaX
    position.y = event.pageY - originPositionRef.deltaY
  }
}
function getOffset(node) {
  const box = node.getBoundingClientRect()
  const docElem = document.documentElement
  // < ie8 不支持 win.pageXOffset, 则使用 docElem.scrollLeft
  return {
    left:
      box.left +
      (window.pageXOffset || docElem.scrollLeft) -
      (docElem.clientLeft || document.body.clientLeft || 0),
    top:
      box.top +
      (window.pageYOffset || docElem.scrollTop) -
      (docElem.clientTop || document.body.clientTop || 0)
  }
}

// 拖拽结束
const handleMouseUp = () => {
  if (props.visible && isDragging.value) {
    const width = imgRef.value?.authImhRef?.offsetWidth * currentScale.value
    const height = imgRef.value?.authImhRef?.offsetHeight * currentScale.value
    const { left, top } = getOffset(imgRef.value?.authImhRef)
    const isRotate = currentRotate.value % 180 !== 0
    isDragging.value = false
    const fixState = getFixScaleEleTransPosition(
      isRotate ? height : width,
      isRotate ? width : height,
      left,
      top
    )
    if (fixState) {
      position.x = fixState.x === 0 ? 0 : fixState.x || position.x
      position.y = fixState.y === 0 ? 0 : fixState.y || position.y
    }
  }
}
onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', handleMouseUp)
})
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', handleMouseUp)
})
defineExpose({ currentMsgId })
</script>
<style scoped lang="scss">
@import '../../index.scss';
.container {
  --webkit-user-select: none;
  --moz-user-select: none;
  --ms-user-select: none;
  user-select: none;

  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  .header {
    display: flex;
    height: 48px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    color: rgba(255, 255, 255, 0.8);
    font-size: 20px;
    background-color: rgba(0, 0, 0, 0.1);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9998;
    .icon {
      padding: 20px;
    }
  }
  .content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100vh;
    .image {
      position: absolute;
      inset: 0;
      transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1) 0s;
      display: flex;
      justify-content: center;
      align-items: center;
      img {
        max-width: 100%;
        max-height: 100%;
        vertical-align: middle;
        transform: scale3d(1, 1, 1);
        cursor: grab;
        transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1) 0s;
        user-select: none;
        pointer-events: auto;
      }
    }
    .arrow {
      width: 56px;
      height: 56px;
      font-size: 16px;
      display: flex;
      justify-content: center;
      align-items: center;
      .icon {
        width: 56px;
        height: 56px;
        z-index: 6666;
      }
    }
    .leftArrow {
      position: absolute;
      left: 20px;
    }
    .rightArrow {
      position: absolute;
      right: 20px;
    }
    .mouseBan {
      cursor: not-allowed;
    }
    .mouseAllow {
      cursor: pointer;
    }
  }
}
</style>
